import React, { useState } from 'react'
import GoBack from 'components/GoBack'
import { RouteConfigComponentProps } from 'react-router-config'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import FitContentPaper from 'components/Paper/FitContentPaper'
import { FormikHelpers } from 'formik'
import { getFieldErrors } from 'utils/api'
import { offeringAddThunk, selectOfferingStatus } from 'store/reducers/temple/offeringSlice'
import { IOfferingFormTimeType, IOfferingFormType } from 'types/store/temple'
import FormOffering from './FormOffering'
import { formToOffering } from 'utils/temple/offering'
import { unwrapResult } from '@reduxjs/toolkit'
import { selectCurrentTemple } from 'store/reducers/temple/templeSlice'
import { selectFeeSplitNameByContentObject } from 'store/reducers/report_app/feeSplitNameSlice'
import { FeeSplitConfigEntryType } from 'api/report_app/report_app.base'
import { feeSplitConfigCreateThunk } from 'store/reducers/report_app/feeSplitConfigSlice'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'
import useRestricter from 'hooks/useRestricter'
import { offeringPoolCreateThunk, OfferingPoolResponseType } from 'store/reducers/temple/offeringPoolSlice'
import { format } from 'date-fns'
import { apiTimeFormat } from 'shared/constants/AppConst'



const AddOffering: React.FC<RouteConfigComponentProps> = (props) => {
    const api = useAppSelector(selectOfferingStatus)
    const dispatch = useAppDispatch()
    const [alerts, setAlerts] = useState<TqAlert[]>([])
    const temple = useAppSelector(selectCurrentTemple)
    const feeSplitNames = useAppSelector(state => selectFeeSplitNameByContentObject(state, temple?.url ?? ''))
    const hasPermission = useRestricter(PERMISSIONS.ADD, RESTRICTED_PATHS.OFFERINGS)

    const handleSubmit = async (data: IOfferingFormType, actions: FormikHelpers<IOfferingFormType>) => {
        setAlerts([])
        let offeringPool: OfferingPoolResponseType | undefined
        if (data.poolConnections.length === 0 && data.offeringTimes.times.length === 0) {
            offeringPool = await dispatch(offeringPoolCreateThunk({
                poolName: `${data.name}-pool`,
                poolLimit: data.perDayLimit ? data.perDayLimit.toString() : undefined
            })).then(unwrapResult)
        }

        async function processOfferingTimes(arr: IOfferingFormTimeType[]) {
            // Map each item to an API call
            const updated = await Promise.all(
                arr.map(async (item) => {
                    if (item.poolConnections.length > 0) return item
                    const pool = await dispatch(offeringPoolCreateThunk({
                        poolName: `${data.name}_${format(item.fromTime.setSeconds(0, 0), apiTimeFormat)}-${item.toTime ? format(item.toTime.setSeconds(0, 0), apiTimeFormat) : ''}_pool`,
                        poolLimit: item.maxLimit ? item.maxLimit.toString() : undefined
                    })).then(unwrapResult)
                    return ({ ...item, poolConnections: [pool.url] }) // modify item with API result
                })
            );
            return updated;
        }
        const modifiedOfferingTimes = await processOfferingTimes(data.offeringTimes.times)
        dispatch(offeringAddThunk(formToOffering(data, modifiedOfferingTimes, offeringPool ? [offeringPool.url] : undefined)))
            .then(unwrapResult)
            .then(async response => {
                setAlerts(a => [
                    ...a,
                    { message: `Offering ${response.name} Successfully created`, severity: 'success' }
                ])
                if (data.splitEntries) {
                    const entries: FeeSplitConfigEntryType[] = []
                    for (const item of data.splitEntries) {
                        if (item.percent != "") {
                            entries.push({ name: item.name, percent: item.percent })
                        }
                    }
                    await dispatch(feeSplitConfigCreateThunk({
                        contentObject: response.url,
                        entries: entries
                    })).then(unwrapResult)
                        .catch(err => {
                            setAlerts(a => [
                                ...a,
                                { message: 'Error updating split fee', severity: 'error' }
                            ])
                        })
                }
                actions.resetForm()
            })
            .catch(err => {
                Object.entries(getFieldErrors(data, err)).forEach(([key, value]) => {
                    setAlerts(a => [
                        ...a,
                        { message: 'Error adding offering', severity: 'error' }
                    ])
                    // actions.setFieldError(key, value))
                    // console.log([key, value])
                })
            })
            .finally(() => {
                actions.setSubmitting(false)
            })
    }

    if (!hasPermission) return null

    return (
        <GoBack {...props}>
            <FitContentPaper>
                {/* <Box component="h4" mb={4}><IntlMessages id="offering.create" /></Box> */}
                <FormOffering
                    feeSplitNames={feeSplitNames}
                    temple={temple}
                    buttonLabelId="common.create"
                    api={api}
                    onSubmitCb={handleSubmit}
                    alerts={alerts}
                />
            </FitContentPaper>
        </GoBack>
    )
}

export default AddOffering